import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ShopifySettings.css';
import { useTranslation } from 'react-i18next';
import { 
    FaShopify, 
    FaPercentage, 
    FaSave, 
    FaSpinner,
    FaExclamationTriangle,
    FaCheck,
    FaEdit
} from 'react-icons/fa';

const ShopifySettings = () => {
    const [user, setUser] = useState(null);
    const [discountPercentage, setDiscountPercentage] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formError, setFormError] = useState(null);
    const [formSuccess, setFormSuccess] = useState(null);
    const [settingsData, setSettingsData] = useState(null);
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchData = async () => {
            try {
                setIsLoading(true);
                await Promise.all([
                    fetchUserData(token),
                    fetchDiscountPercentage(token)
                ]);
            } catch (error) {
                console.error('Error loading Shopify settings:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [navigate]);

    const fetchUserData = async (token) => {
        try {
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            setUser(null);
            navigate('/');
        }
    };

    const fetchDiscountPercentage = async (token) => {
        try {
            const response = await axios.get('/api/shopify_settings/get_discount_percentage', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.status === 'success') {
                setDiscountPercentage(response.data.discount_percentage || 0);
                setSettingsData(response.data);
            } else {
                setDiscountPercentage(0);
                setSettingsData(null);
            }
        } catch (error) {
            console.error('Failed to fetch discount percentage:', error);
            setDiscountPercentage(0);
            setSettingsData(null);
        }
    };

    const canManageShopifySettings = () => {
        return user && ['admin', 'seller', 'eshop'].includes(user.role);
    };

    const handleEdit = () => {
        if (!canManageShopifySettings()) return;
        setIsEditing(true);
        setFormError(null);
        setFormSuccess(null);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setFormError(null);
        setFormSuccess(null);
        // Reset to original value
        setDiscountPercentage(settingsData?.discount_percentage || 0);
    };

    const handleInputChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        setDiscountPercentage(value);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setFormError(null);
        setFormSuccess(null);

        const token = localStorage.getItem('access_token');

        // Validate discount percentage
        if (discountPercentage < 0 || discountPercentage > 100) {
            setFormError(t('invalidDiscountPercentage', 'Discount percentage must be between 0 and 100'));
            setIsSaving(false);
            return;
        }

        try {
            const response = await axios.post('/api/shopify_settings/update_discount_percentage', {
                discount_percentage: discountPercentage
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setFormSuccess(t('discountPercentageUpdated', 'Discount percentage updated successfully!'));
            setIsEditing(false);
            
            // Refresh data
            await fetchDiscountPercentage(token);
            
            // Clear success message after 3 seconds
            setTimeout(() => {
                setFormSuccess(null);
            }, 3000);
        } catch (error) {
            console.error('Failed to update discount percentage:', error);
            setFormError(
                error.response?.data?.detail || 
                t('failedToUpdateDiscountPercentage', 'Failed to update discount percentage. Please try again.')
            );
        } finally {
            setIsSaving(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="una-shopify-settings-loading">
                <FaSpinner className="una-spinner" />
                <h2>{t('loadingShopifySettings', 'Loading Shopify settings...')}</h2>
            </div>
        );
    }

    if (!canManageShopifySettings()) {
        return (
            <div className="una-shopify-settings-unauthorized">
                <FaExclamationTriangle className="una-warning-icon" />
                <h2>{t('accessDenied', 'Access Denied')}</h2>
                <p>{t('noPermissionShopifySettings', 'You do not have permission to manage Shopify settings.')}</p>
            </div>
        );
    }

    return (
        <div className="una-shopify-settings-container">
            <div className="una-shopify-settings-header">
                <div className="una-shopify-settings-title">
                    <FaShopify className="una-shopify-icon" />
                    <h1>{t('shopifySettings', 'Shopify Settings')}</h1>
                </div>
            </div>

            <div className="una-shopify-settings-content">
                <div className="una-settings-card">
                    <div className="una-settings-card-header">
                        <div className="una-settings-title-section">
                            <h3>
                                <FaPercentage className="una-percentage-icon" />
                                {t('discountPercentage', 'Discount Percentage')}
                            </h3>
                            <p className="una-settings-description">
                                {t('discountPercentageDescription', 'Configure the default discount percentage for your Shopify store.')}
                            </p>
                        </div>
                        
                        {!isEditing && (
                            <button
                                className="una-edit-button"
                                onClick={handleEdit}
                                title={t('editDiscountPercentage', 'Edit Discount Percentage')}
                            >
                                <FaEdit />
                                {t('edit', 'Edit')}
                            </button>
                        )}
                    </div>
                    
                    <div className="una-settings-card-content">
                        {isEditing ? (
                            <form onSubmit={handleSave} className="una-discount-form">
                                <div className="una-form-group">
                                    <label htmlFor="discountPercentage">
                                        {t('discountPercentage', 'Discount Percentage')} (0-100) *
                                    </label>
                                    <div className="una-percentage-input-group">
                                        <input
                                            type="number"
                                            id="discountPercentage"
                                            value={discountPercentage}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            required
                                            className="una-percentage-input"
                                            placeholder="0.0"
                                        />
                                        <span className="una-percentage-symbol">%</span>
                                    </div>
                                </div>

                                {formError && <p className="una-error-message">{formError}</p>}
                                {formSuccess && <p className="una-success-message">{formSuccess}</p>}

                                <div className="una-form-actions">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="una-button una-button-secondary"
                                    >
                                        {t('cancel', 'Cancel')}
                                    </button>
                                    <button
                                        type="submit"
                                        className="una-button una-button-primary"
                                        disabled={isSaving}
                                    >
                                        {isSaving ? (
                                            <>
                                                <FaSpinner className="una-spinner" />
                                                {t('saving', 'Saving...')}
                                            </>
                                        ) : (
                                            <>
                                                <FaSave />
                                                {t('save', 'Save')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="una-discount-display">
                                <div className="una-current-value">
                                    <span className="una-value-label">{t('currentValue', 'Current Value')}: </span>
                                    <span className="una-value-display">
                                        {discountPercentage}%
                                    </span>
                                </div>
                                
                                {settingsData && (
                                    <div className="una-settings-meta">
                                        {settingsData.created_at && (
                                            <span className="una-meta-item">
                                                {t('created', 'Created')}: {formatDate(settingsData.created_at)}
                                            </span>
                                        )}
                                        {settingsData.updated_at && (
                                            <span className="una-meta-item">
                                                {t('lastUpdated', 'Last Updated')}: {formatDate(settingsData.updated_at)}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {formSuccess && !isEditing && (
                <div className="una-success-banner">
                    <FaCheck className="una-success-icon" />
                    <span>{formSuccess}</span>
                </div>
            )}
        </div>
    );
};

export default ShopifySettings;